import apiClient from './client'
import type {
  Digest,
  DigestListResponse,
  GenerateDigestRequest,
  SendDigestRequest,
} from '@/types'

export const digestApi = {
  /**
   * List dates with digest availability
   * @param startDate Optional start date (YYYY-MM-DD)
   * @param endDate Optional end date (YYYY-MM-DD)
   * @param limit Number of dates to return (default 30)
   */
  listDates: async (
    startDate?: string,
    endDate?: string,
    limit?: number
  ): Promise<DigestListResponse> => {
    const params: Record<string, string | number> = {}
    if (startDate) params.start_date = startDate
    if (endDate) params.end_date = endDate
    if (limit) params.limit = limit

    const response = await apiClient.get<DigestListResponse>('/digest/list', { params })
    return response.data
  },

  /**
   * Get digest for a specific date
   * @param date Date in YYYY-MM-DD format
   */
  getByDate: async (date: string): Promise<Digest> => {
    const response = await apiClient.get<Digest>(`/digest/${date}`)
    return response.data
  },

  /**
   * Get rendered HTML for a digest
   * @param date Date in YYYY-MM-DD format
   */
  getHTML: async (date: string): Promise<string> => {
    const response = await apiClient.get<string>(`/digest/${date}/html`, {
      responseType: 'text',
    })
    return response.data
  },

  /**
   * Generate a new digest for a date
   * @param request Contains date and optional force flag
   */
  generate: async (request: GenerateDigestRequest): Promise<Digest> => {
    const response = await apiClient.post<Digest>('/digest/generate', request)
    return response.data
  },

  /**
   * Send digest to Rocket.Chat
   * @param date Date in YYYY-MM-DD format
   * @param request Contains list of recipients
   */
  sendToRocket: async (date: string, request: SendDigestRequest): Promise<void> => {
    await apiClient.post(`/digest/${date}/send`, request)
  },

  /**
   * Delete a digest
   * @param date Date in YYYY-MM-DD format
   */
  delete: async (date: string): Promise<void> => {
    await apiClient.delete(`/digest/${date}`)
  },
}
